import { z } from 'zod';
import { UserPhotoSchema } from '../../models/user';
import { ErrorResponseSchema } from '../../common/base';
import { USERNAME_MAX_LENGTH } from '../../../utils/username-validation';
import {
  emailValidator,
  phoneValidator,
  dateValidatorNullable,
  nonNegativeIntValidator,
} from '../../common/validators';

/**
 * Request schema for updating user profile
 * POST /api/user/profile
 *
 * All fields are optional - only provided fields will be updated
 */
export const UpdateProfileRequestSchema = z.object({
  /** User's first name */
  firstName: z.string().max(100).optional(),
  /** User's last name */
  lastName: z.string().max(100).optional(),
  /** User's email address */
  email: z.string().email('Invalid email format').optional(),
  /** User's phone number */
  phone: z
    .string()
    .refine(
      (val) => val === '' || /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(val),
      'Invalid phone number format'
    )
    .optional(),
  // `username` is deliberately absent. It used to be accepted and written
  // straight through with no uniqueness check, which let one seller take
  // another's public URL. Changes now go through /api/user/change-username.
  // Zod strips the key, so an old client sending it is ignored rather than
  // rejected — nothing else in the request needs to fail over a stale field.
  /** Shipping format preference */
  shippingFormat: z.string().max(15).optional(),
  /** Default USPS parcel template for new listings; null clears it */
  defaultParcelTemplate: z.string().max(40).nullable().optional(),
});

export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

/**
 * Profile schema for the update profile response
 * Uses string ID to match what the endpoint returns
 */
export const UpdateProfileUserSchema = z.object({
  /** User ID (as string) */
  id: z.string(),
  /** The seller's public store address, `/s/{username}` */
  username: z.string().max(USERNAME_MAX_LENGTH).nullable(),
  /** User email address */
  email: emailValidator,
  /** User's first name */
  firstName: z.string().nullable(),
  /** User's last name */
  lastName: z.string().nullable(),
  /** Whether user has uploaded a profile photo */
  hasPhoto: z.number().int().min(0).max(1),
  /** User trust level (0-10 scale) */
  trustLevel: nonNegativeIntValidator,
  /** User account status */
  status: z.number().int().min(0).max(255),
  /** User profile photos */
  photos: UserPhotoSchema.optional(),
  /** User phone number */
  phone: phoneValidator,
  /** Whether phone number has been verified */
  isPhoneVerified: z.boolean(),
  /** Shipping format preference */
  shippingFormat: z.string().max(15).nullable(),
  /** Default USPS parcel template for new listings */
  defaultParcelTemplate: z.string().max(40).nullable().optional(),
  /** Last profile update timestamp */
  updatedDate: dateValidatorNullable,
  /** User type (buyer/seller) */
  userType: z.number().int().nullable(),
  /** Membership type (free/premium) */
  membershipType: z.number().int().nullable(),
});

export type UpdateProfileUser = z.infer<typeof UpdateProfileUserSchema>;

/**
 * Success response schema for profile update
 * Returns the full updated user profile
 */
const UpdateProfileSuccessSchema = z.object({
  success: z.literal(true),
  /** Updated user profile with all fields */
  profile: UpdateProfileUserSchema,
});

export type UpdateProfileSuccess = z.infer<typeof UpdateProfileSuccessSchema>;

/**
 * Combined response schema for profile update endpoint
 * Discriminated union based on success field
 *
 * @example
 * ```typescript
 * const response = UpdateProfileResponseSchema.parse(data);
 * if (response.success) {
 *   console.log('Profile updated:', response.profile);
 * } else {
 *   console.error('Error:', response.error);
 * }
 * ```
 */
export const UpdateProfileResponseSchema = z.discriminatedUnion('success', [
  UpdateProfileSuccessSchema,
  ErrorResponseSchema,
]);

export type UpdateProfileResponse = z.infer<typeof UpdateProfileResponseSchema>;
